import { APP_CONFIG } from "../constants/appConstants";
import { auth, onAuthStateChanged } from "./firebaseApi";

function waitForAuthUser(timeoutMs = 8000) {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsub();
      reject(new Error("Authentication not ready. Please retry."));
    }, timeoutMs);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        clearTimeout(timer);
        unsub();
        resolve(user);
      }
    });
  });
}

async function authedFetch(path, options = {}) {
  const user = await waitForAuthUser();
  // Use cached token by default. Forcing refresh on every call hits Firebase auth/quota-exceeded.
  let token = await user.getIdToken(false);
  const buildHeaders = (idToken) => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${idToken}`,
    ...(options.headers || {}),
  });
  const timeoutMs = Number(options.timeoutMs) || 0;
  const retries = Number(options.retries) || 0;
  const { timeoutMs: _t, retries: _r, ...fetchOptions } = options;

  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = timeoutMs > 0 ? new AbortController() : null;
    const timer =
      controller && timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const res = await fetch(`${APP_CONFIG.ADMIN_API_BASE_URL}${path}`, {
        ...fetchOptions,
        headers: buildHeaders(token),
        signal: controller?.signal,
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 && attempt === 0) {
        token = await user.getIdToken(true);
        continue;
      }
      if (!res.ok) {
        const err = new Error(data?.error || data?.message || "Request failed");
        err.code = data?.code;
        err.status = res.status;
        throw err;
      }
      return data;
    } catch (error) {
      lastError = error;
      const quota =
        error?.code === "auth/quota-exceeded" ||
        String(error?.message || "").includes("auth/quota-exceeded");
      if (quota) {
        throw new Error(
          "Firebase Auth quota exceeded (too many token refreshes). Wait a minute and try again — this is fixed in the latest app build."
        );
      }
      const isAbort = error?.name === "AbortError";
      const isNetwork = isAbort || error?.message === "Failed to fetch";
      if (attempt < retries && isNetwork) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        continue;
      }
      if (isAbort) {
        throw new Error("Server is waking up — please try again in a few seconds.");
      }
      throw error;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  throw lastError;
}

export const userApiService = {
  getProfile() {
    return authedFetch("/app/profile");
  },
  saveProfile(payload) {
    return authedFetch("/app/profile", { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteProfile() {
    return authedFetch("/app/profile", { method: "DELETE" });
  },
  listNotifications(params = {}) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v) !== "") search.set(k, String(v));
    });
    const qs = search.toString();
    return authedFetch(`/app/notifications${qs ? `?${qs}` : ""}`);
  },
  listDevices() {
    return authedFetch("/app/devices");
  },
  addDevice(payload) {
    return authedFetch("/app/devices", { method: "POST", body: JSON.stringify(payload) });
  },
  updateDevice(deviceId, payload) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  linkHubNode(hubId, payload) {
    return authedFetch(`/app/devices/${encodeURIComponent(hubId)}/nodes`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  addHubSimNodes(hubId) {
    return authedFetch(`/app/devices/${encodeURIComponent(hubId)}/sim-nodes`, { method: "POST" });
  },
  unlinkHubNode(hubId, nodeId) {
    return authedFetch(
      `/app/devices/${encodeURIComponent(hubId)}/nodes/${encodeURIComponent(nodeId)}`,
      { method: "DELETE" }
    );
  },
  getDeviceConfig(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/config`);
  },
  getDeviceLive(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/live`);
  },
  getDeviceHistory(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/history`);
  },
  requestWifiScan(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/request-wifi-scan`, { method: "POST" });
  },
  sendDeviceCommand(deviceId, payload) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/commands`, {
      method: "POST",
      body: JSON.stringify(payload),
      timeoutMs: 45000,
      retries: 1,
    });
  },
  listDeviceCommands(deviceId, params = {}) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v) !== "") search.set(k, String(v));
    });
    const qs = search.toString();
    return authedFetch(
      `/app/devices/${encodeURIComponent(deviceId)}/commands${qs ? `?${qs}` : ""}`
    );
  },
  pushDeviceConfig(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/push-config`, { method: "POST" });
  },
  deleteDevice(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}`, { method: "DELETE" });
  },
  listFirmwareOffers() {
    return authedFetch("/app/firmware/offers");
  },
  acceptFirmwareOffer(releaseId, deviceIds = []) {
    return authedFetch(`/app/firmware/offers/${encodeURIComponent(releaseId)}/accept`, {
      method: "POST",
      body: JSON.stringify({ deviceIds }),
    });
  },
  rejectFirmwareOffer(releaseId, deviceIds = []) {
    return authedFetch(`/app/firmware/offers/${encodeURIComponent(releaseId)}/reject`, {
      method: "POST",
      body: JSON.stringify({ deviceIds }),
    });
  },
  getAlertRuleSelections() {
    return authedFetch("/app/alert-rules");
  },
  getAlertRules() {
    return authedFetch("/app/alert-rules");
  },
  saveAlertRuleSelections(selections) {
    return authedFetch("/app/alert-rules", {
      method: "PUT",
      body: JSON.stringify({ selections }),
    });
  },
  upsertAlertRule(templateId, payload = true) {
    const body = typeof payload === "boolean" ? { enabled: payload } : payload;
    return authedFetch(`/app/alert-rules/${encodeURIComponent(templateId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  deleteAlertRule(templateId) {
    return authedFetch(`/app/alert-rules/${encodeURIComponent(templateId)}`, {
      method: "DELETE",
    });
  },
  listIrrigationSchedules(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/irrigation-schedules`);
  },
  createIrrigationSchedule(deviceId, payload) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/irrigation-schedules`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  updateIrrigationSchedule(deviceId, scheduleId, payload) {
    return authedFetch(
      `/app/devices/${encodeURIComponent(deviceId)}/irrigation-schedules/${encodeURIComponent(scheduleId)}`,
      { method: "PATCH", body: JSON.stringify(payload) }
    );
  },
  deleteIrrigationSchedule(deviceId, scheduleId) {
    return authedFetch(
      `/app/devices/${encodeURIComponent(deviceId)}/irrigation-schedules/${encodeURIComponent(scheduleId)}`,
      { method: "DELETE" }
    );
  },
  getIrrigationAutoRule(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/irrigation-auto-rule`);
  },
  saveIrrigationAutoRule(deviceId, payload) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/irrigation-auto-rule`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },
  deleteIrrigationAutoRule(deviceId) {
    return authedFetch(`/app/devices/${encodeURIComponent(deviceId)}/irrigation-auto-rule`, {
      method: "DELETE",
    });
  },
  listPendingIrrigationConsent() {
    return authedFetch("/app/irrigation-consent/pending");
  },
  approveIrrigationConsent(requestId) {
    return authedFetch(`/app/irrigation-consent/${encodeURIComponent(requestId)}/approve`, {
      method: "POST",
    });
  },
  denyIrrigationConsent(requestId) {
    return authedFetch(`/app/irrigation-consent/${encodeURIComponent(requestId)}/deny`, {
      method: "POST",
    });
  },
  registerPushToken(payload) {
    return authedFetch("/app/push-token", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  unregisterPushToken(payload) {
    return authedFetch("/app/push-token", {
      method: "DELETE",
      body: JSON.stringify(payload),
    });
  },
};

